/**
 * De berichten in de chat, met de mogelijkheid om een stuk tekst te
 * selecteren en daar uitleg over te vragen aan Socrates.
 *
 * De selectie werkt zo: laat de leerling de muis los boven de berichten,
 * dan verschijnt boven de selectie een knopje. Klikken erop vraagt de
 * uitleg; klikken ergens anders laat het knopje verdwijnen.
 */
import React, { useEffect, useRef, useState } from 'react';
import { BookOpen } from 'lucide-react';
import { kleur } from '../../../theme/tokens';

export function ChatBerichten({ berichten, bezig, activePersona, onVraagUitleg }) {
  const [selectie, setSelectie] = useState('');
  const [popup, setPopup] = useState(null); // { x, y } of null
  const eindRef = useRef(null);
  const vakRef = useRef(null);

  // Naar het laatste bericht scrollen als er een bij komt -- niet bij het
  // openen van de stap, want dan zou de pagina verspringen bij elke keer
  // terugkomen uit de argumenten.
  const eersteKeer = useRef(true);
  useEffect(() => {
    if (eersteKeer.current) { eersteKeer.current = false; return; }
    eindRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [berichten]);

  // Sluit het knopje bij een klik ergens anders.
  useEffect(() => {
    const sluit = () => { if (popup) { setPopup(null); setSelectie(''); } };
    document.addEventListener('click', sluit);
    return () => document.removeEventListener('click', sluit);
  }, [popup]);

  const bijSelectie = () => {
    const selection = window.getSelection();
    const tekst = selection.toString().trim();
    if (tekst && tekst.length > 0 && tekst.length < 200) {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      // Alleen een selectie binnen de berichten telt.
      if (vakRef.current && vakRef.current.contains(range.commonAncestorContainer)) {
        setSelectie(tekst);
        setPopup({ x: rect.left + rect.width / 2, y: rect.top - 10 });
      }
    } else {
      setPopup(null);
      setSelectie('');
    }
  };

  const vraagUitleg = async () => {
    if (!selectie || bezig) return;
    setPopup(null);
    try {
      await onVraagUitleg(selectie);
    } finally {
      setSelectie('');
      window.getSelection().removeAllRanges();
    }
  };

  const stijlVan = (msg) =>
    msg.type === 'user' ? styles.userMessage :
    msg.type === 'uitleg' ? styles.uitlegMessage :
    msg.type === 'taalcorrectie' ? styles.correctieMessage :
    styles.personaMessage;

  return (
    <>
      <div style={styles.chatMessages} ref={vakRef} onMouseUp={bijSelectie}>
        <div style={styles.selectHint}>
          <BookOpen size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
          Tip: Selecteer tekst om uitleg te vragen aan Socrates
        </div>
        {berichten.map((msg, idx) => (
          <div key={idx} style={stijlVan(msg)}>
            {msg.type !== 'user' && <span style={msg.type === 'uitleg' ? styles.messageFromUitleg : styles.messageFrom}>{msg.from}</span>}
            <p style={styles.messageText}>{msg.text}</p>
            {msg.type === 'user' && <span style={styles.messageTo}>→ {msg.to}</span>}
          </div>
        ))}
        {bezig && <div style={styles.typing}>{activePersona?.naam || 'Socrates'} is aan het typen...</div>}
        <div ref={eindRef} />
      </div>

      {popup && (
        <div style={{ ...styles.explainPopup, left: popup.x, top: popup.y }} onClick={(e) => e.stopPropagation()}>
          <button onClick={vraagUitleg} style={styles.explainBtn}>
            <BookOpen size={14} style={{ marginRight: '6px' }} />
            Vraag uitleg aan Socrates
          </button>
        </div>
      )}
    </>
  );
}

const bericht = { background: 'white', color: 'black', padding: '12px 16px', borderRadius: '8px', marginBottom: '12px', maxWidth: '80%', border: '1px solid black' };

const styles = {
  chatMessages: { flex: 1, padding: '20px', overflowY: 'auto', maxHeight: '400px' },
  userMessage: { ...bericht, marginLeft: 'auto' },
  personaMessage: bericht,
  uitlegMessage: { ...bericht, background: kleur.geelLicht, border: `2px solid ${kleur.geel}`, whiteSpace: 'pre-wrap' },
  correctieMessage: { alignSelf: 'center', maxWidth: '90%', background: kleur.amber, border: `1px solid ${kleur.amberLijn}`, color: kleur.amberDonker, padding: '12px 16px', borderRadius: '12px' },
  messageFrom: { fontSize: '12px', fontWeight: 'bold', color: 'black', marginBottom: '4px', display: 'block' },
  messageFromUitleg: { fontSize: '12px', fontWeight: 'bold', color: kleur.geelDonker, marginBottom: '4px', display: 'block' },
  messageText: { margin: 0, lineHeight: 1.5 },
  messageTo: { fontSize: '11px', opacity: 0.8, marginTop: '4px', display: 'block', textAlign: 'right' },
  typing: { color: kleur.stil, fontStyle: 'italic', padding: '10px' },
  selectHint: { textAlign: 'center', padding: '8px 16px', background: kleur.vlakGrijsLicht, borderRadius: '20px', fontSize: '12px', color: kleur.stil, marginBottom: '16px', display: 'inline-block', marginLeft: '50%', transform: 'translateX(-50%)' },
  explainPopup: { position: 'fixed', transform: 'translateX(-50%) translateY(-100%)', zIndex: 1000, background: 'white', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', padding: '4px' },
  explainBtn: { display: 'flex', alignItems: 'center', padding: '8px 16px', background: kleur.geel, color: kleur.tekst, border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', whiteSpace: 'nowrap' },
};
